import React from 'react';
import {
  getFeesBySolutionTotalPaid,
  getFeesBySolutionTotalPrice,
  getInvoiceCapabilities,
  getOverallMaxPrice,
  getSolutionPrice,
  useFeesBySolutionContext
} from 'sonata-platform-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import { FeeSolution } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolution.schema';
import { FeeSolutionPlan } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolutionPlan.schema';

import CapabilitiesInvoiceView from './CapabilitiesInvoice.view';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  id: React.ComponentProps<typeof CapabilitiesInvoiceView>['id'];
  selectedSolution?: FeeSolution;
};

type ViewType = typeof CapabilitiesInvoiceView;

const CapabilitiesInvoiceHOC = (ViewComponent: ViewType): React.VFC<Props> => {
  const WrapperComponent = ({ currencyInfo, id, selectedSolution }: Props) => {
    const { campaign, capabilities, capabilitiesByUser, capabilityCategories, getStep3, plans, user } =
      useFeesBySolutionContext();

    const { extraCapabilities = [], selectedPlan } = getStep3();

    const solutionName = selectedSolution && selectedSolution.copy;
    const solutionPrice = getSolutionPrice(selectedPlan, selectedSolution);
    const pricingModel = selectedPlan?.pricingModel || selectedSolution?.pricingModel;

    const plan = plans?.find((item: FeeSolutionPlan) => item._id === selectedPlan?.plan);

    const capabilitiesFilterByPlan = capabilities?.filter(({ _id }: { _id: string }) =>
      plan!.extraCapabilities.includes(_id!)
    );
    const capabilitiesByUserFilterByPlan = capabilitiesByUser?.filter(
      ({ capability, feeSolution }: { capability: string; feeSolution: string }) =>
        plan!.extraCapabilities.includes(capability) && feeSolution === plan!.feeSolution
    );

    const categoriesPrice = getInvoiceCapabilities({
      capabilities: capabilitiesFilterByPlan,
      capabilitiesByUser: capabilitiesByUserFilterByPlan,
      capabilityCategories,
      extraCapabilities,
      pricingModel
    });
    const totalPrice = getFeesBySolutionTotalPrice({
      capabilities: capabilitiesFilterByPlan,
      capabilitiesByUser: capabilitiesByUserFilterByPlan,
      capabilityCategories,
      extraCapabilities,
      selectedPlan,
      selectedSolution
    });
    const overallMaxPrice = getOverallMaxPrice(selectedPlan, selectedSolution);
    const totalPaid = getFeesBySolutionTotalPaid({
      campaign,
      capabilities: capabilitiesFilterByPlan,
      capabilitiesByUser: capabilitiesByUserFilterByPlan,
      capabilityCategories,
      extraCapabilities,
      selectedPlan,
      selectedSolution,
      user
    });
    const formatPrice = (value: number) =>
      normalizeWithUnit(value, currencyInfo, 2).replace(
        currencyInfo.currencySymbol,
        ` ${currencyInfo.currencySymbol} `
      );

    return (
      <ViewComponent
        categoriesPrice={categoriesPrice}
        formatPrice={formatPrice}
        id={id}
        overallMaxCost={overallMaxPrice}
        plan={plan}
        solutionName={solutionName || ''}
        solutionPrice={solutionPrice}
        totalPaid={totalPaid}
        totalPrice={totalPrice}
      />
    );
  };

  return WrapperComponent;
};

export default CapabilitiesInvoiceHOC;
